/**
 * Error classification and message generation for streaming chat
 */

const ERROR_TYPES = {
  NETWORK: 'network',
  PARSE: 'parse',
  SERVER: 'server',
  UNKNOWN: 'unknown'
};

const ERROR_MESSAGES = {
  [ERROR_TYPES.NETWORK]: "Connection lost. Would you like to try again?",
  [ERROR_TYPES.SERVER]: "I encountered an issue. Please try your question again.",
  [ERROR_TYPES.PARSE]: "Processing error occurred.",
  [ERROR_TYPES.UNKNOWN]: "Something went wrong. Please try again."
};

/**
 * Classifies streaming errors into specific types
 * @param {Error} error - The error to classify
 * @returns {{type: string, message: string, retryable: boolean}} Error classification
 */
export const classifyStreamingError = (error) => {
  // Network errors
  if (
    error.name === 'NetworkError' ||
    error.name === 'TimeoutError' ||
    error.name === 'AbortError' ||
    error.message.includes('network') ||
    error.message.includes('timeout') ||
    !navigator.onLine
  ) {
    return {
      type: ERROR_TYPES.NETWORK,
      message: ERROR_MESSAGES[ERROR_TYPES.NETWORK],
      retryable: true
    };
  }

  // Parse errors
  if (
    error.name === 'SyntaxError' ||
    error.message.includes('JSON') ||
    error.message.includes('parse')
  ) {
    return {
      type: ERROR_TYPES.PARSE,
      message: ERROR_MESSAGES[ERROR_TYPES.PARSE],
      retryable: false
    };
  }

  // Server errors
  if (
    error.status >= 500 ||
    error.message.includes('server') ||
    error.message.includes('5') // Matches 500, 502, 503, etc.
  ) {
    return {
      type: ERROR_TYPES.SERVER,
      message: ERROR_MESSAGES[ERROR_TYPES.SERVER],
      retryable: true
    };
  }

  // Unknown errors
  return {
    type: ERROR_TYPES.UNKNOWN,
    message: ERROR_MESSAGES[ERROR_TYPES.UNKNOWN],
    retryable: true
  };
};

/**
 * Logs error details to console for debugging
 * @param {Error} error - The error to log
 * @param {Object} context - Additional context information
 */
export const logStreamingError = (error, context = {}) => {
  const errorInfo = {
    timestamp: new Date().toISOString(),
    errorName: error.name,
    errorMessage: error.message,
    errorStack: error.stack,
    ...context
  };

  console.error('[Streaming Error]:', errorInfo);
};

export { ERROR_TYPES }; 